interface Finding {
  pattern: string
  severity: string
  example: string
}

// Pattern type from scan.patterns (can be string or object)
interface PatternItem {
  type?: string
  severity?: string
  description?: string
  count?: number
  examples?: string[]
}

interface PDFData {
  scanId: string
  score: number
  riskLevel: string
  /** Real message count, or null when unknown — never fabricated. */
  messageCount: number | null
  summary?: string
  findings: Finding[]
  recommendations: string[]
  createdAt: string
  // Accept patterns as fallback when findings is empty
  patterns?: (string | PatternItem)[]
  // Accept raw analysis object for extracting messages/findings directly
  analysis?: {
    summary?: string
    findings?: Finding[]
    recommendations?: string[]
    messages?: any[]
    [key: string]: any
  } | null
}

// Color tokens matching the app design system
const COLORS = {
  high: { r: 220, g: 38, b: 38 }, // red-600
  medium: { r: 217, g: 119, b: 6 }, // amber-600
  low: { r: 5, g: 150, b: 105 }, // emerald-600
  slate900: { r: 15, g: 23, b: 42 },
  slate700: { r: 51, g: 65, b: 85 },
  slate500: { r: 100, g: 116, b: 139 },
  slate400: { r: 148, g: 163, b: 184 },
  slate200: { r: 226, g: 232, b: 240 },
  slate100: { r: 241, g: 245, b: 249 },
  white: { r: 255, g: 255, b: 255 },
}

function getSeverityColor(severity: string) {
  const s = severity.toLowerCase()
  if (s === 'high' || s === 'high risk') return COLORS.high
  if (s === 'medium' || s === 'medium risk') return COLORS.medium
  return COLORS.low
}

// Derive risk level from score as a fallback safety net
function deriveRiskLevel(score: number, providedRiskLevel: string): string {
  // If a valid risk level was provided, use it
  const normalized = providedRiskLevel?.toLowerCase()
  if (
    normalized === 'high' ||
    normalized === 'medium' ||
    normalized === 'low'
  ) {
    return normalized
  }
  // Otherwise derive from score
  if (score >= 70) return 'high'
  if (score >= 40) return 'medium'
  return 'low'
}

function setColor(doc: jsPDF, color: { r: number; g: number; b: number }) {
  doc.setTextColor(color.r, color.g, color.b)
}

function drawLine(doc: jsPDF, y: number, margin: number, pageWidth: number) {
  doc.setDrawColor(COLORS.slate200.r, COLORS.slate200.g, COLORS.slate200.b)
  doc.setLineWidth(0.3)
  doc.line(margin, y, pageWidth - margin, y)
}

function addFooter(doc: jsPDF, pageNum: number, totalPages: number) {
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const margin = 20

  // Footer line
  drawLine(doc, pageHeight - 18, margin, pageWidth)

  // Left: branding
  doc.setFontSize(7)
  doc.setFont('helvetica', 'normal')
  setColor(doc, COLORS.slate400)
  doc.text('TOXICDETECTOR.AI — CONFIDENTIAL', margin, pageHeight - 12)

  // Right: page number
  doc.text(
    `Page ${pageNum} of ${totalPages}`,
    pageWidth - margin,
    pageHeight - 12,
    { align: 'right' },
  )
}

function checkPageBreak(
  doc: jsPDF,
  currentY: number,
  requiredSpace: number,
  margin: number,
): number {
  const pageHeight = doc.internal.pageSize.getHeight()
  if (currentY + requiredSpace > pageHeight - 25) {
    doc.addPage()
    return margin + 10
  }
  return currentY
}

export const generateCaseFilePDF = async (data: PDFData): Promise<void> => {
  return generatePDF(data)
}

export const generatePDF = async (data: PDFData): Promise<void> => {
  try {
    // Dynamically import jsPDF to reduce initial bundle size
    const { default: jsPDF } = await import('jspdf')

    // Create new document
    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const margin = 20
    const contentWidth = pageWidth - margin * 2
    // Ensure risk level is valid — derive from score if needed
    const resolvedRiskLevel = deriveRiskLevel(data.score, data.riskLevel)
    const riskColor = getSeverityColor(resolvedRiskLevel)

    // Resolve findings from multiple data sources (in priority order):
    // 1. Explicitly passed findings array
    // 2. analysis.findings from the raw analysis object
    // 3. analysis.messages converted to findings (Edge Function async result)
    // 4. patterns array converted to findings (scan.patterns fallback)
    let effectiveFindings = data.findings

    if (!effectiveFindings || effectiveFindings.length === 0) {
      if (data.analysis?.findings && data.analysis.findings.length > 0) {
        effectiveFindings = data.analysis.findings
      } else if (data.analysis?.messages && data.analysis.messages.length > 0) {
        effectiveFindings = convertMessagesToFindings(data.analysis.messages)
      } else if (data.patterns && data.patterns.length > 0) {
        effectiveFindings = convertPatternsToFindings(data.patterns)
      }
    }

    // Resolve summary from analysis object if not explicitly provided
    const effectiveSummary = data.summary || data.analysis?.summary || undefined

    // Resolve recommendations from analysis object if not explicitly provided
    const effectiveRecommendations =
      data.recommendations.length > 0
        ? data.recommendations
        : data.analysis?.recommendations || []

    // ═══════════════════════════════════════════
    // PAGE 1: COVER
    // ═══════════════════════════════════════════

    // Top accent bar
    doc.setFillColor(riskColor.r, riskColor.g, riskColor.b)
    doc.rect(0, 0, pageWidth, 4, 'F')

    // Classification label
    doc.setFontSize(8)
    doc.setFont('helvetica', 'bold')
    setColor(doc, COLORS.slate400)
    doc.text('CONFIDENTIAL — FOR PERSONAL USE ONLY', margin, 20)

    // Main title
    doc.setFontSize(32)
    doc.setFont('helvetica', 'bold')
    setColor(doc, COLORS.slate900)
    doc.text('FORENSIC', margin, 50)
    doc.text('COMMUNICATION', margin, 62)
    doc.text('ANALYSIS', margin, 74)

    // Subtitle
    doc.setFontSize(11)
    doc.setFont('helvetica', 'normal')
    setColor(doc, COLORS.slate500)
    doc.text('AI-Powered Behavioral Pattern Detection Report', margin, 86)

    // Divider
    drawLine(doc, 95, margin, pageWidth)

    // Metadata grid
    let metaY = 108
    const col1 = margin
    const col2 = margin + 85

    doc.setFontSize(8)
    doc.setFont('helvetica', 'bold')
    setColor(doc, COLORS.slate400)
    doc.text('CASE IDENTIFIER', col1, metaY)
    doc.text('DATE OF ANALYSIS', col2, metaY)

    metaY += 6
    doc.setFontSize(11)
    doc.setFont('helvetica', 'normal')
    setColor(doc, COLORS.slate900)
    doc.text(data.scanId.substring(0, 8).toUpperCase(), col1, metaY)
    doc.text(
      new Date(data.createdAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
      col2,
      metaY,
    )

    metaY += 14
    doc.setFontSize(8)
    doc.setFont('helvetica', 'bold')
    setColor(doc, COLORS.slate400)
    doc.text('MESSAGES ANALYZED', col1, metaY)
    doc.text('RISK CLASSIFICATION', col2, metaY)

    metaY += 6
    doc.setFontSize(11)
    doc.setFont('helvetica', 'normal')
    setColor(doc, COLORS.slate900)
    doc.text(
      data.messageCount ? `${data.messageCount}` : 'Conversation analyzed',
      col1,
      metaY,
    )

    // Risk level with color
    setColor(doc, riskColor)
    doc.setFont('helvetica', 'bold')
    doc.text(resolvedRiskLevel.toUpperCase() + ' RISK', col2, metaY)

    // Score display — large centered block
    const scoreY = 170
    doc.setFillColor(COLORS.slate100.r, COLORS.slate100.g, COLORS.slate100.b)
    doc.roundedRect(margin, scoreY - 5, contentWidth, 50, 4, 4, 'F')

    doc.setFontSize(48)
    doc.setFont('helvetica', 'bold')
    setColor(doc, COLORS.slate900)
    doc.text(`${data.score}`, pageWidth / 2 - 10, scoreY + 25, {
      align: 'center',
    })

    doc.setFontSize(14)
    setColor(doc, COLORS.slate500)
    doc.setFont('helvetica', 'normal')
    doc.text('/100 TOXICITY SCORE', pageWidth / 2 + 15, scoreY + 25)

    // Bottom accent
    doc.setFillColor(riskColor.r, riskColor.g, riskColor.b)
    doc.rect(margin, scoreY + 48, contentWidth, 1.5, 'F')

    // Branding at bottom
    doc.setFontSize(9)
    doc.setFont('helvetica', 'bold')
    setColor(doc, COLORS.slate900)
    doc.text('ToxicDetector.ai', margin, pageHeight - 30)

    doc.setFontSize(7)
    doc.setFont('helvetica', 'normal')
    setColor(doc, COLORS.slate400)
    doc.text(
      'This report is generated by AI analysis and does not constitute a clinical diagnosis.',
      margin,
      pageHeight - 24,
    )
    doc.text(
      'For professional support, please consult a licensed therapist.',
      margin,
      pageHeight - 19,
    )

    // ═══════════════════════════════════════════
    // PAGE 2: EXECUTIVE SUMMARY
    // ═══════════════════════════════════════════
    doc.addPage()
    let y = 20

    // Top accent
    doc.setFillColor(riskColor.r, riskColor.g, riskColor.b)
    doc.rect(0, 0, pageWidth, 2, 'F')

    // Section header
    doc.setFontSize(8)
    doc.setFont('helvetica', 'bold')
    setColor(doc, COLORS.slate400)
    doc.text('SECTION 01', margin, y)

    y += 10
    doc.setFontSize(22)
    doc.setFont('helvetica', 'bold')
    setColor(doc, COLORS.slate900)
    doc.text('Executive Summary', margin, y)

    y += 5
    drawLine(doc, y, margin, pageWidth)
    y += 12

    // Summary text - use resolved summary or generate default
    const summaryText =
      effectiveSummary ||
      getDefaultSummary(
        resolvedRiskLevel,
        effectiveFindings.length,
        data.messageCount,
      )

    doc.setFontSize(11)
    doc.setFont('helvetica', 'normal')
    setColor(doc, COLORS.slate700)
    const summaryLines = doc.splitTextToSize(summaryText, contentWidth)
    doc.text(summaryLines, margin, y)
    y += summaryLines.length * 6 + 10

    // Key metrics boxes
    y = checkPageBreak(doc, y, 40, margin)
    drawLine(doc, y, margin, pageWidth)
    y += 10

    doc.setFontSize(8)
    doc.setFont('helvetica', 'bold')
    setColor(doc, COLORS.slate400)
    doc.text('KEY METRICS', margin, y)
    y += 10

    // Metrics row - use effectiveFindings count
    const metricWidth = contentWidth / 3
    const metrics = [
      { label: 'Toxicity Score', value: `${data.score}/100` },
      { label: 'Patterns Detected', value: `${effectiveFindings.length}` },
      {
        label: 'Risk Level',
        value: resolvedRiskLevel.toUpperCase(),
        color: riskColor,
      },
    ]

    metrics.forEach((metric, i) => {
      const x = margin + i * metricWidth

      doc.setFontSize(20)
      doc.setFont('helvetica', 'bold')
      if (metric.color) {
        setColor(doc, metric.color)
      } else {
        setColor(doc, COLORS.slate900)
      }
      doc.text(metric.value, x, y)

      doc.setFontSize(8)
      doc.setFont('helvetica', 'normal')
      setColor(doc, COLORS.slate500)
      doc.text(metric.label, x, y + 6)
    })

    y += 20

    // Recommendations section
    if (effectiveRecommendations.length > 0) {
      y = checkPageBreak(doc, y, 50, margin)
      drawLine(doc, y, margin, pageWidth)
      y += 10

      doc.setFontSize(8)
      doc.setFont('helvetica', 'bold')
      setColor(doc, COLORS.slate400)
      doc.text('RECOMMENDATIONS', margin, y)
      y += 10

      effectiveRecommendations.forEach((rec) => {
        y = checkPageBreak(doc, y, 12, margin)

        // Bullet
        doc.setFillColor(riskColor.r, riskColor.g, riskColor.b)
        doc.circle(margin + 2, y - 1.5, 1.2, 'F')

        doc.setFontSize(10)
        doc.setFont('helvetica', 'normal')
        setColor(doc, COLORS.slate700)
        const recLines = doc.splitTextToSize(rec, contentWidth - 10)
        doc.text(recLines, margin + 8, y)
        y += recLines.length * 5 + 4
      })
    }

    // ═══════════════════════════════════════════
    // PAGE 3+: DETAILED PATTERNS (FINDINGS)
    // ═══════════════════════════════════════════
    if (effectiveFindings.length > 0) {
      doc.addPage()
      y = 20

      // Top accent
      doc.setFillColor(riskColor.r, riskColor.g, riskColor.b)
      doc.rect(0, 0, pageWidth, 2, 'F')

      doc.setFontSize(8)
      doc.setFont('helvetica', 'bold')
      setColor(doc, COLORS.slate400)
      doc.text('SECTION 02', margin, y)

      y += 10
      doc.setFontSize(22)
      doc.setFont('helvetica', 'bold')
      setColor(doc, COLORS.slate900)
      doc.text('Detailed Pattern Analysis', margin, y)

      y += 5
      drawLine(doc, y, margin, pageWidth)
      y += 10

      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')
      setColor(doc, COLORS.slate500)
      doc.text(
        `${effectiveFindings.length} communication pattern${effectiveFindings.length !== 1 ? 's' : ''} identified during analysis.`,
        margin,
        y,
      )
      y += 12

      // Render each finding
      effectiveFindings.forEach((finding, index) => {
        const sevColor = getSeverityColor(finding.severity)

        // Estimate space needed: header (10) + example (20-40) + padding (15)
        const exampleText = finding.example
          ? finding.example.replace(/\n\n/g, '\n')
          : ''
        const exampleLines = exampleText
          ? doc.splitTextToSize(exampleText, contentWidth - 16)
          : []
        const neededSpace = 30 + exampleLines.length * 5 + 15

        y = checkPageBreak(doc, y, neededSpace, margin)

        // If we just broke to a new page, re-add the accent bar
        if (y < margin + 15) {
          doc.setFillColor(riskColor.r, riskColor.g, riskColor.b)
          doc.rect(0, 0, pageWidth, 2, 'F')
        }

        // Card background
        doc.setFillColor(
          COLORS.slate100.r,
          COLORS.slate100.g,
          COLORS.slate100.b,
        )
        const cardHeight =
          22 + (exampleLines.length > 0 ? exampleLines.length * 5 + 12 : 0)
        doc.roundedRect(margin, y - 2, contentWidth, cardHeight, 2, 2, 'F')

        // Left severity accent bar
        doc.setFillColor(sevColor.r, sevColor.g, sevColor.b)
        doc.rect(margin, y - 2, 2, cardHeight, 'F')

        // Pattern number + name
        doc.setFontSize(7)
        doc.setFont('helvetica', 'bold')
        setColor(doc, COLORS.slate400)
        doc.text(
          `PATTERN ${String(index + 1).padStart(2, '0')}`,
          margin + 6,
          y + 4,
        )

        doc.setFontSize(13)
        doc.setFont('helvetica', 'bold')
        setColor(doc, COLORS.slate900)
        doc.text(finding.pattern, margin + 6, y + 12)

        // Severity badge
        const badgeText = finding.severity.toUpperCase()
        const badgeWidth = doc.getTextWidth(badgeText) + 8
        const badgeX = pageWidth - margin - badgeWidth - 4

        doc.setFillColor(sevColor.r, sevColor.g, sevColor.b)
        doc.roundedRect(badgeX, y + 1, badgeWidth, 7, 2, 2, 'F')

        doc.setFontSize(7)
        doc.setFont('helvetica', 'bold')
        setColor(doc, COLORS.white)
        doc.text(badgeText, badgeX + 4, y + 6)

        // Example evidence
        if (exampleText && exampleLines.length > 0) {
          const quoteY = y + 18

          // Quote accent line
          doc.setFillColor(
            COLORS.slate200.r,
            COLORS.slate200.g,
            COLORS.slate200.b,
          )
          doc.rect(margin + 8, quoteY - 2, 1, exampleLines.length * 5 + 2, 'F')

          doc.setFontSize(9)
          doc.setFont('helvetica', 'italic')
          setColor(doc, COLORS.slate700)
          doc.text(exampleLines, margin + 14, quoteY + 2)
        }

        y += cardHeight + 8
      })
    }

    // ═══════════════════════════════════════════
    // ADD FOOTERS TO ALL PAGES
    // ═══════════════════════════════════════════
    const totalPages = doc.getNumberOfPages()
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i)
      addFooter(doc, i, totalPages)
    }

    // ═══════════════════════════════════════════
    // SAVE
    // ═══════════════════════════════════════════
    const fileName = `ToxicDetector-CaseFile-${data.scanId.substring(0, 8).toUpperCase()}.pdf`
    doc.save(fileName)
  } catch (error) {
    console.error('Error generating PDF:', error)
    throw error
  }
}

// Convert patterns array to findings format for PDF rendering
// Includes ALL examples and description for complete reports
function convertPatternsToFindings(
  patterns: (string | PatternItem)[],
): Finding[] {
  return patterns.map((pattern) => {
    if (typeof pattern === 'string') {
      return {
        pattern: pattern,
        severity: 'MEDIUM',
        example: '',
      }
    }
    // Build a comprehensive example string from all available data
    const parts: string[] = []
    if (pattern.description) {
      parts.push(pattern.description)
    }
    if (pattern.examples && pattern.examples.length > 0) {
      pattern.examples.forEach((ex, i) => {
        if (ex && ex.trim()) {
          parts.push(ex.trim())
        }
      })
    }
    if (pattern.count && pattern.count > 1) {
      parts.push(`(Detected ${pattern.count} times)`)
    }
    return {
      pattern: pattern.type || 'Unknown Pattern',
      severity: (pattern.severity || 'MEDIUM').toUpperCase(),
      example: parts.join('\n\n'),
    }
  })
}

// Convert raw analysis messages to findings format
// Used when the Edge Function stores messages[] instead of findings[]
function convertMessagesToFindings(messages: any[]): Finding[] {
  // Group messages by their flags to create pattern-based findings
  const patternMap = new Map<
    string,
    { severity: number; examples: string[]; reasons: string[] }
  >()

  messages.forEach((msg: any) => {
    const flags = msg.flags || []
    flags.forEach((flag: string) => {
      if (!patternMap.has(flag)) {
        patternMap.set(flag, { severity: 0, examples: [], reasons: [] })
      }
      const entry = patternMap.get(flag)!
      entry.severity = Math.max(entry.severity, msg.severity || 0)
      if (msg.text && entry.examples.length < 3) {
        entry.examples.push(msg.text)
      }
      if (msg.short_reason && !entry.reasons.includes(msg.short_reason)) {
        entry.reasons.push(msg.short_reason)
      }
    })
  })

  return Array.from(patternMap.entries()).map(([flag, data]) => {
    const severityLabel =
      data.severity >= 70 ? 'HIGH' : data.severity >= 40 ? 'MEDIUM' : 'LOW'
    const parts: string[] = []
    if (data.reasons.length > 0) {
      parts.push(data.reasons[0])
    }
    data.examples.forEach((ex) => {
      if (ex.trim()) parts.push(ex.trim())
    })
    return {
      pattern: flag,
      severity: severityLabel,
      example: parts.join('\n\n'),
    }
  })
}

function getDefaultSummary(
  riskLevel: string,
  patternCount: number,
  messageCount: number | null,
): string {
  const scope = messageCount
    ? `This forensic analysis of ${messageCount} messages`
    : 'This forensic analysis of the conversation'
  if (riskLevel === 'high') {
    return `${scope} has identified ${patternCount} distinct communication patterns that indicate a high-risk dynamic. The patterns detected suggest recurring manipulative, dismissive, or coercive behaviors that may be harmful to the recipient's emotional well-being. Immediate attention and boundary-setting are strongly recommended. The detailed findings below provide specific examples extracted from the conversation.`
  }
  if (riskLevel === 'medium') {
    return `${scope} has identified ${patternCount} communication patterns that warrant attention. While not immediately alarming, these patterns suggest areas where the communication dynamic could be improved. Monitoring these behaviors over time is recommended to determine if they escalate or resolve. The detailed findings below provide specific examples from the conversation.`
  }
  return `${scope} found ${patternCount > 0 ? patternCount + ' minor patterns' : 'no significant concerning patterns'}. Overall, the communication dynamic appears healthy and respectful. The conversation demonstrates balanced engagement and appropriate emotional expression. Continue fostering open dialogue and addressing minor concerns as they arise.`
}
